import { useId, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";

interface FieldShellProps {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean | undefined;
}

function describedBy(id: string, error?: string, hint?: string) {
  return [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined;
}

function Shell({ id, label, error, hint, required, children }: FieldShellProps & { id: string; children: ReactNode }) {
  return (
    <div className="cc-field">
      <label className="cc-field__label" htmlFor={id}>
        {label}
        {required ? <span className="cc-field__required" aria-hidden="true"> *</span> : null}
      </label>
      {children}
      {hint ? (
        <p className="cc-field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="cc-field__error" id={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

export type TextFieldProps = FieldShellProps & Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "required">;

export function TextField({ label, error, hint, required, className, ...input }: TextFieldProps) {
  const id = useId();
  return (
    <Shell id={id} label={label} error={error} hint={hint} required={required}>
      <input
        id={id}
        className={["cc-input", className].filter(Boolean).join(" ")}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        aria-required={required || undefined}
        {...input}
      />
    </Shell>
  );
}

export type SelectFieldProps = FieldShellProps & Omit<SelectHTMLAttributes<HTMLSelectElement>, "id" | "required">;

/** Native select: best keyboard, screen-reader and iPad picker behaviour for free. */
export function SelectField({ label, error, hint, required, children, ...select }: SelectFieldProps) {
  const id = useId();
  return (
    <Shell id={id} label={label} error={error} hint={hint} required={required}>
      <select
        id={id}
        className="cc-input cc-select"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        aria-required={required || undefined}
        {...select}
      >
        {children}
      </select>
    </Shell>
  );
}

export type TextAreaFieldProps = FieldShellProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "required"> & { ref?: Ref<HTMLTextAreaElement> };

export function TextAreaField({ label, error, hint, required, className, ...area }: TextAreaFieldProps) {
  const id = useId();
  return (
    <Shell id={id} label={label} error={error} hint={hint} required={required}>
      <textarea
        id={id}
        className={["cc-input", "cc-textarea", className].filter(Boolean).join(" ")}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        aria-required={required || undefined}
        {...area}
      />
    </Shell>
  );
}
